import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { DealSalesScope } from '@/lib/graphql/generated/graphql';
import {
  type SalesSelection,
  type ManagerOption,
  selectedManagerIds,
} from '@/states/deals-list.state';
import { ChevronDown, Users } from 'lucide-react';

export function SalesFilter({
  selection,
  managers,
  onChange,
}: {
  selection: SalesSelection;
  managers: ManagerOption[];
  onChange: (selection: SalesSelection) => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (managers.length === 0 && selection.salesScope !== DealSalesScope.Me) {
      onChange({ salesScope: DealSalesScope.Me, managerIds: [] });
    }
  }, [managers.length, selection.salesScope, onChange]);
  const selected = selectedManagerIds(selection, managers);
  const me = managers
    .filter((manager) => manager.isMe)
    .map((manager) => manager.id);
  const isMe =
    selection.salesScope === DealSalesScope.Me ||
    (me.length > 0 &&
      selected.length === me.length &&
      me.every((id) => selected.includes(id)));
  const label = isMe
    ? t('deals-list.me')
    : selection.salesScope === DealSalesScope.All
      ? t('deals-list.all')
      : t('deals-list.selected', { count: selected.length });
  const toggle = (id: string) => {
    if (selected.includes(id) && selected.length === 1) return;
    const ids = selected.includes(id)
      ? selected.filter((item) => item !== id)
      : [...selected, id];
    if (
      ids.length === managers.length &&
      managers.every((manager) => ids.includes(manager.id))
    ) {
      onChange({ salesScope: DealSalesScope.All, managerIds: [] });
    } else if (
      me.length > 0 &&
      ids.length === me.length &&
      me.every((item) => ids.includes(item))
    ) {
      onChange({ salesScope: DealSalesScope.Me, managerIds: [] });
    } else {
      onChange({ salesScope: DealSalesScope.Selected, managerIds: ids });
    }
  };
  // An unavailable directory leaves ME usable without guessing owners or widening it.
  if (managers.length === 0)
    return (
      <span className="rounded-md border px-4 py-2 text-sm">
        {t('deals-list.sales')}: {t('deals-list.me')}
      </span>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Users />
          {t('deals-list.sales')}: {label}
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <div className="max-h-72 overflow-y-auto">
          {managers.map((manager) => {
            const name =
              manager.id === 'unknown' || manager.name === 'Unknown'
                ? t('deals-list.unknown')
                : manager.name;
            const checked = selected.includes(manager.id);
            return (
              <DropdownMenuCheckboxItem
                key={manager.id}
                checked={checked}
                disabled={checked && selected.length === 1}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => toggle(manager.id)}
              >
                <Avatar className="mr-2 h-6 w-6 text-[10px]">
                  <AvatarImage src={manager.photoUrl ?? undefined} alt="" />
                  <AvatarFallback>
                    {name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span>{name}</span>
                {manager.isMe && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {t('deals-list.me')}
                  </span>
                )}
              </DropdownMenuCheckboxItem>
            );
          })}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            onChange({ salesScope: DealSalesScope.All, managerIds: [] })
          }
        >
          {t('deals-list.selectAll')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            onChange({ salesScope: DealSalesScope.Me, managerIds: [] })
          }
        >
          {t('deals-list.onlyMe')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
